// Temp: maps variant color slugs to a preview color for the swatch picker.
// Slugs are sourced from the product export (Slug column → trailing color
// segment). Each entry is either a hand-picked hex (sampled from real product
// photos) or a fallback to one of the canonical colors in COLOR_HEX_MAP.
export const COLOR_HEX_MAP: Record<string, string> = {
  black: '#111111',
  white: '#f5f5f5',
  grey: '#7a7a7e',
  gray: '#7a7a7e',
  red: '#b3261e',
  bikingred: '#b3261e',
  blue: '#282e3f',
  midnightblue: '#3c4355',
  alaskanblue: '#a3c4dc',
  green: '#3a6b3e',
  pink: '#e8a2bf',
  cream: '#efe6d1',
  beige: '#d8c9a8',
  brown: '#6a4730',
  wood: '#8a6a45',
  yellow: '#ecca4a',
  purple: '#6d5480',
  quietshade: '#7a7a7e',
  baw: '#cccccc',
}

export const COLOR_ALIASES: Record<string, string> = {
  // ── blue ─────────────────────────────────────────────────────────────
  babyblue: '#b8c1d4',
  bluelight: '#c7d6e6',
  chalkblue: '#a8a6b8',
  crystalblue: '#a2c0d9',
  darkblue: '#1e2228',
  duskblue: '#62617d', // Pantone 18-3817 TPG Heron
  heron: '#62617d', // Pantone 18-3817 TPG Heron
  iceblue: '#c1dde5',
  mistyblue: '#737f89',
  powderblue: '#9faec7',
  royalblue: '#3345a1',
  saluteblue: '#2d3247',
  skyblue: 'blue',
  slateblue: '#373c45',
  softpetrol: '#1e4143',
  steelblue: '#8e9cbe',
  stormblue: '#232b3c',
  strongblue: '#046cce',
  vintageblue: '#3c4b67',
  vividblue: '#0287cf',

  // ── navy / midnight blue ─────────────────────────────────────────────
  dustynavy: '#232a3c',
  estateblue: '#283e70',
  midnightnavy: '#1a1b2d',
  navy: '#181b2f',
  navyblue: '#232b3c',
  nightfall: '#1c2129',
  nightfallblue: '#1c2129',
  nightfallrose: '#1c2129',
  oceannavy: '#22243e',
  velvetnavy: '#15425d',

  // ── green ────────────────────────────────────────────────────────────
  armygreen: '#4d492b',
  brookgreen: '#82c3ae',
  darkgreen: '#2f493c',
  deepgreen: '#0e3f3c',
  emeraldgreen: '#0f6c57',
  emeraldshade: '#182829',
  evergreen: '#1e523c',
  forest: 'green',
  forestgreen: '#1d3229',
  greenmist: '#8a9a91', // Pantone 16-5804 TPX Slate Gray
  icegreen: '#c4e4d6',
  mint: '#83c6ad',
  mistygreen: '#556864',
  olive: 'green',
  olivesmoke: '#3b3e2c',
  peppergreen: '#019d73',
  poolgreen: '#08beac',
  sagegreen: '#90a47d',
  softkhaki: '#4f5146',
  stonegreen: '#2e433c',
  urbangreen: '#234233',

  // ── pink ─────────────────────────────────────────────────────────────
  babypink: '#dda3b8',
  blossompink: '#dac6c9',
  blush: 'pink',
  blushpink: '#d4a7b6',
  bubblegum: '#cd6f9a',
  candypink: '#d4bec0',
  geraniumpink: '#e295a3',
  lavenderfrost: '#d4b8d0',
  lilacmist: '#b09fb5',
  mauveblush: '#a5929d', // Pantone 16-3304 TCX Sea Fog
  pastellilac: '#ab9abf',
  peachblush: '#d6948b',
  peony: '#e29db4',
  rose: 'pink',
  rosepink: '#e8c8d7',
  salmonpink: 'pink',
  salmonpinkmarl: '#d9a98d',
  softcoral: '#d78473',
  strawberrymoonpink: '#e283c5',
  woodrose: '#a27270',

  // ── red / wine / burgundy / plum ─────────────────────────────────────
  blackcherry: '#300e1a',
  bordo: '#8d0f25',
  burgudy: 'red',
  burgundy: 'red',
  cherry: '#8d111a',
  deepberry: '#650722',
  deepplum: '#39191c',
  elderberry: '#8d6368',
  firered: '#e12a36',
  granita: '#a1023d',
  highriskred: '#cd0a1c',
  maroon: '#6b212a',
  merlot: '#56151d',
  plumdust: '#471c2f',
  steelviolet: '#6d6db3',
  stormviolet: '#51485d',
  twilightplum: '#25102c',
  velvetpurple: '#361623',
  vintageviolet: '#62383c',
  violet: '#4c334b',
  watermelon: '#b3142b',
  wine: 'red',
  wineplum: '#7d494d',

  // ── brown / chocolate / espresso ─────────────────────────────────────
  chocolate: '#3e1d1c',
  chocolatebrown: '#533b34',
  cocoabrown: '#35231f',
  coffee: 'brown',
  coffeebrown: '#2a1f1d',
  espresso: '#5f3c33',
  esresso: 'brown',
  milktea: '#ccb9ab',
  mochamist: '#6f5b52',
  rawhidebrown: '#946645',
  smokybrown: '#987167',
  toffee: '#5c3f36',

  // ── wood / caramel / tan ─────────────────────────────────────────────
  camel: 'wood',
  carameldust: '#765d4d',
  goldenhaze: '#d8b78c',
  toffeetan: '#542918',
  winewood: '#3c1e20',

  // ── beige / sand / stone ─────────────────────────────────────────────
  almondblush: '#ad957f',
  beachbeige: '#ab8f8b',
  desert: '#d4b38a',
  desertsand: '#e1c2b0',
  frostedalmond: '#f1d8b9',
  khaki: 'beige',
  oatmilk: '#e2d2ba',
  powderpuffbeige: '#e6dbcb',
  purplebeige: '#967d78',
  sand: '#cec5c3',
  sandbeige: '#9a816d',
  sandsmoke: '#b3978d',
  softbeige: '#baa9a2',
  stone: '#4f4d4d',
  stonebeige: '#7d6a5c',
  tan: 'beige',
  taupebeige: '#7b6857',
  truetaupe: '#6e5b55',
  vanillabeige: '#edd7c2',
  whitebeige: 'beige',

  // ── cream / ivory ────────────────────────────────────────────────────
  creme: '#cac4b3',
  ivory: 'cream',
  ivoryshadow: '#0c0c0c',
  skylinecream: '#d8c4ae',
  vanillacream: '#e6d297',

  // ── grey ─────────────────────────────────────────────────────────────
  asphaltgrey: '#57555a',
  charcoal: '#bbb1b0',
  coolgrey: '#69626a',
  irongrey: '#2d2d34',
  lavendergrey: '#563d40',
  marlgrey: '#bfbab8',
  marlgreyblue: '#bfbab8',
  marlgreygreen: '#bfbab8',
  marlgreygrey: '#bfbab8',
  marlgreyred: '#bfbab8',
  marlgreywhite: '#c1b7b8',
  midnightmarlgrey: '#d0c9c8',
  pewtergrey: '#3e3c42',
  silver: 'grey',
  softgrey: '#c5c3c2',
  stonegray: '#39343b',
  stonegrey: '#5f5b5c',

  // ── white / off-white ────────────────────────────────────────────────
  crystalwhite: '#ddd5d3',
  milkwhite: '#f2ebdf',
  offwhite: 'white',
  pearlwhite: '#f2f0eb', // Pantone 11-0602 TCX Snow White
  powderwhite: '#d4c6bb',

  // ── printed white tees ───────────────────────────────────────────────
  // Oversized Printed / Essential tees are all a white garment carrying a
  // differently-coloured print, so the swatch is white tinted towards that
  // print. A flat 'white' for each would collapse them: the card swatch row
  // dedupes on hex (useCollectionSwatches), so identical values would hide
  // every colourway but the first. `whiteblack` above stays pure white and
  // covers the black-print one.
  bowwhite: '#f2ccd8', // pink bow print
  cherrywhite: '#f2d3cc', // red "Exalted Club" script
  whitepink: '#f9e3ec',
  whiteviolet: '#ddd7ee',
  whiteyellow: '#f2e8b8',

  // ── black / noir ─────────────────────────────────────────────────────
  blackgrey: '#0e0e0e',
  blackmilkwhite: '#0e0e0e',
  blackwhite: '#0e0e0e',
  blackwithgrey: '#0e0e0e',
  moonblack: '#53515f',
  noirblack: '#0e0e0e',
  stonenoir: '#50484c',
  whiteblack: '#ffffff',

  // ── yellow / champagne ───────────────────────────────────────────────
  butteryellow: '#decc9e',
  champagne: '#9c8074',
  gold: 'yellow',
  lemonglow: '#dbce88',
  lemonzest: '#d8b221',
  mustard: 'yellow',

  // ── non-colour trailing tokens ───────────────────────────────────────
  // The Digital Gift Card has no colour: its slug ends `…-gift-card` and its
  // SKUs end `…-GIFTCARD-CZ-<amount>`, so the derivation lands on `card`
  // (slug path) or `cz` (SKU path, the segment before the amount). Both point
  // at the same product; map them to black so it renders as the card artwork
  // does instead of the grey unknown-colour fallback.
  card: 'black',
  cz: 'black',

  // ── mixed swatches (best-guess primary) ──────────────────────────────
  bebrpi: '#bb8682',
  greyivory: 'grey',
  oatmilkblue: '#d5c9ba',
  oatmilkpink: '#d7c6b2',
  whblbe: '#ffffff',
}

// White and off-white swatches (`white` #f5f5f5, `whblbe` #ffffff, `milkwhite`
// #f2ebdf, `cream` #efe6d1) disappear when they sit on the white card/page and
// their only outline is their own color — callers ring those with `--line`
// instead. The 0.9 cut sits just under `cream` and above `crystalwhite`
// (#ddd5d3 ≈ 0.84), which reads fine unringed.
export function isNearWhite(hex: string): boolean {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!match)
    return false
  const int = Number.parseInt(match[1]!, 16)
  const r = (int >> 16) & 0xFF
  const g = (int >> 8) & 0xFF
  const b = int & 0xFF
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 > 0.9
}

export function colorHex(name: string): string {
  const key = name.toLowerCase()
  if (COLOR_HEX_MAP[key])
    return COLOR_HEX_MAP[key]
  const alias = COLOR_ALIASES[key]
  if (!alias)
    return '#cccccc'
  return alias.startsWith('#') ? alias : (COLOR_HEX_MAP[alias] ?? '#cccccc')
}
